import React from 'react';

import { Image, Title, LinkText, Button, Message } from 'common/components';
import { Glossary, ContractGL } from 'common/utils/glossary';
import { ExpirationType, checkExpirationDatetime } from 'common/utils/date';
import { StateType } from 'common/modules/domain/purchase';
import { useWindowSize } from 'react-use';

interface ContractPlanProps {
  /** 契約中のプラン画像のURL */
  imageSrc: string;
  /** 契約中のプラン名 */
  contractPlanName: string;
  /** 契約中プランの購入日時 */
  operationDatetime: string;
  /** 契約中プランの次回更新 */
  expirationDatetime: string | null;
  /** 契約中プランの猶予期間 */
  expirationGraceDays: number | null;
  /** 契約中プランの税込み価格 */
  contractTaxIncludedPrice: number | null;
  /** 購入番号 */
  vendorPurchaseId: string;
  /** 期間 */
  validityPeriod: number | null;
  /** 利用制限付き売り切りプランの有効期限 */
  perpetualValidityPeriod: number | null;
  /** 利用回数 */
  usage: number | null;
  /** 利用上限 */
  limit: number | null;
  /** 通貨 */
  currency: string | null;
  /** デモプランか否か */
  isDemo: boolean;
  /** 解約済みか否か */
  isCanceled: boolean;
  /** プランの状態("contract"：契約中 or "cancel"：解約 or "demo"：デモ or "demo_canceled": デモ解約 or "individual_cancel" : 個別解約) */
  state: StateType;
  /** デバイス制限上限 */
  planLimit: number | null;
  /** デバイス利用量 */
  planUsage: number | null;
  /** デバイス制限の種別(現状は1：端末数のみ)。デバイス対応プランでない場合は属性なし。 */
  deviceLimitType: number | null;
  /** 第三者利用登録の種別 */
  lendingType: number | null;
  /** 貸与数 */
  lendingQuantity: number | null;
  /** 購入数 */
  purchaseQuantity: number | null;
  /** 貸与状態 */
  lendingStatus: string | null;
  /** サブスクリプション更新種別 */
  renewalType: string | null;
  /** 詳細を見るをクリック時のコールバック関数 */
  onClickDetail: () => void;
  /** 解約をクリック時のコールバック関数 */
  onClickCancel: () => void;
  /** 第三者の利用登録をクリック時のコールバック関数 */
  onClickLendingUser: () => void;
  /** デバイス管理をクリック時のコールバック関数 */
  onClickDevice: () => void;
  /** 機能のアクティベートをクリック時のコールバック関数 */
  onClickActivate: () => void;
  /** 利用者の利用中画面 利用解除ボタンをクリック時のコールバック関数 */
  onClickCancellationOfUse: () => void;
  /** E2Eテスト用識別子 */
  'data-test': string;
  /** グロッサリ */
  glossary: Glossary;
  /** 言語 */
  language: string;
}

/**
 * 購入ステータス文字列を作成する
 * - 入力
 *   - validityPeriod: number | null  期間
 *   - expirationDatetime: string | null  契約中プランの次回更新日
 *   - expirationGraceDays: number | null  契約中プランの猶予期間
 *   - state: StateType  契約中プランの状態
 *   - renewalType: string | null  契約中プランの更新種別
 *   - glossary: Glossary  グロッサリ
 * - 戻り値
 *   - 解約されています
 *     - 売り切り＋有効期限内＋解約
 *     - サブスク＋有効期限内＋解約＋自動更新ON
 *     - サブスク＋有効期限内＋解約＋自動更新OFF
 *   - 有効期限が過ぎています
 *     - サブスク＋猶予期間内＋解約＋自動更新ON
 *     - サブスク＋猶予期間内＋解約＋自動更新OFF
 *     - サブスク＋有効期限切れ＋契約＋自動更新ON
 *     - サブスク＋有効期限切れ＋契約＋自動更新OFF
 *     - サブスク＋有効期限切れ＋個別解約＋自動更新ON
 *     - サブスク＋有効期限切れ＋個別解約＋自動更新OFF
 *   - 自動更新が停止中です
 *     - サブスク＋有効期限内＋契約＋自動更新OFF
 *     - サブスク＋有効期限内＋個別解約＋自動更新OFF
 *     - サブスク＋猶予期間内＋契約＋自動更新OFF
 *     - サブスク＋猶予期間内＋個別解約＋自動更新OFF
 *   - '-'
 *     - 上記以外(表示なし用)
 */
const _createPurchaseStatusText = (
  validityPeriod: number | null,
  expirationDatetime: string | null,
  expirationGraceDays: number | null,
  state: StateType,
  renewalType: string | null,
  glossary: Glossary
) => {
  // デフォルト値として「表示しない」場合の文字列を設定
  let purchaseStatusText = '-';
  // 売り切りorサブスクの判断
  if (validityPeriod) {
    // サブスクの場合、有効期限を確認
    const expiration_type = checkExpirationDatetime(
      Date.now(),
      expirationDatetime,
      expirationGraceDays
    );
    // 解約＋有効期限内の場合は「解約されています」を表示
    if (
      state == StateType.TYPE_CANCEL &&
      expiration_type == ExpirationType.TYPE_CONTRACT
    ) {
      purchaseStatusText = glossary.glx(ContractGL.AlreadyCanceled);
    }
    // 以下の場合は「有効期限が過ぎています」を表示
    // ・解約＋猶予期間内
    // ・契約＋有効期限切れ
    // ・個別解約＋有効期限切れ
    if (
      (state == StateType.TYPE_CANCEL &&
        expiration_type == ExpirationType.TYPE_GRACE) ||
      (state == StateType.TYPE_CONTRACT &&
        expiration_type == ExpirationType.TYPE_EXPIRED) ||
      (state == StateType.TYPE_INDIVIDUAL_CANCEL &&
        expiration_type == ExpirationType.TYPE_EXPIRED)
    ) {
      purchaseStatusText = glossary.glx(ContractGL.GracePeriodExpired);
    }
    // 以下の場合は「自動更新が停止中です」を表示
    // ・自動更新OFF＋契約＋有効期限内 or 猶予期間内
    // ・自動更新OFF＋個別解約＋有効期限内 or 猶予期間内
    if (renewalType == 'Manual') {
      if (
        (state == StateType.TYPE_CONTRACT &&
          (expiration_type == ExpirationType.TYPE_CONTRACT ||
            expiration_type == ExpirationType.TYPE_GRACE)) ||
        (state == StateType.TYPE_INDIVIDUAL_CANCEL &&
          (expiration_type == ExpirationType.TYPE_CONTRACT ||
            expiration_type == ExpirationType.TYPE_GRACE))
      ) {
        purchaseStatusText = glossary.glx(ContractGL.ManualUpdate);
      }
    }
  } else {
    // 売り切りの場合、有効期限の有無とプランの状態を確認
    if (expirationDatetime && state == StateType.TYPE_CANCEL) {
      // 売り切りプランの場合は猶予期間が設定されていないため0を指定する
      const expiration_type = checkExpirationDatetime(
        Date.now(),
        expirationDatetime,
        0
      );
      // 解約＋有効期限内の場合は「解約されています」を表示
      if (expiration_type == ExpirationType.TYPE_CONTRACT) {
        purchaseStatusText = glossary.glx(ContractGL.AlreadyCanceled);
      }
    }
  }
  return purchaseStatusText;
};

const ContractPlanInfo: React.FC<ContractPlanProps> = ({
  imageSrc,
  contractPlanName,
  operationDatetime,
  expirationDatetime,
  expirationGraceDays,
  contractTaxIncludedPrice,
  vendorPurchaseId,
  validityPeriod,
  perpetualValidityPeriod,
  usage,
  limit,
  currency,
  isDemo,
  isCanceled,
  state,
  planLimit,
  planUsage,
  deviceLimitType,
  lendingType,
  lendingQuantity,
  purchaseQuantity,
  lendingStatus,
  renewalType,
  onClickDetail,
  onClickCancel,
  onClickLendingUser,
  onClickDevice,
  onClickActivate,
  onClickCancellationOfUse,
  'data-test': dataTest,
  glossary,
  language
}) => {
  const { width, height } = useWindowSize();

  const dispOperationDatetime = operationDatetime
    ? new Intl.DateTimeFormat(language).format(Date.parse(operationDatetime))
    : '-';
  const dispExpirationDatetime = expirationDatetime
    ? new Intl.DateTimeFormat(language).format(Date.parse(expirationDatetime))
    : '-';
  // 貸与ユーザーか否か(貸与ユーザーの場合は料金,注文番号,注文数を表示しない)
  const isLoanUser = lendingStatus != null;
  // 請求日と有効期限のラベルを切り替える
  // デモプラン、解約済みかつ有効期限内、利用制限のある売り切り、貸与ユーザー："有効期限"
  // それ以外："次回請求日"
  const expirationDateTitle =
    isDemo || isCanceled || perpetualValidityPeriod || isLoanUser
      ? glossary.glx(ContractGL.ExpirationDateForDemo)
      : glossary.glx(ContractGL.ExpirationDatetime);
  // 価格をローカライズ化
  const localize_price =
    contractTaxIncludedPrice !== null && currency !== null
      ? contractTaxIncludedPrice.toLocaleString(language, {
          style: 'currency',
          currency: currency
        })
      : '';
  // サブスクリプション有効日数が未定義の場合は売り切りと判断し、グロッサリーを利用しない
  const priceLabel =
    validityPeriod && !isDemo
      ? glossary
          .glx(ContractGL.SubscriptionPrice)
          .replace('{0}', localize_price)
          .replace('{1}', validityPeriod.toString())
      : localize_price;
  // 利用回数と上限
  const usageAndLimit =
    usage != null && usage >= 0 && limit
      ? usage.toString() + ' / ' + limit.toString()
      : '-';
  const isValidSubscriptionItem = validityPeriod && !isCanceled ? true : false;
  // 第三者利用登録可能か否か(第三者利用登録の種別が1の場合は可能)
  const isLendingPlan = lendingType === 1 ? true : false;
  /*
   * 他ユーザー利用の表示有無
   * ・貸与数がnullではない
   * ・購入数がnullではない
   * ・第三者利用登録可能
   * ・貸与ユーザーではない
   */
  const lendingUsageAndLimit =
    lendingQuantity != null &&
    purchaseQuantity != null &&
    isLendingPlan &&
    !isLoanUser
      ? lendingQuantity.toString()
      : null;
  // 利用デバイス数,デバイス管理ボタンの表示有無
  const planUsageAndLimit =
    deviceLimitType == 1 && planUsage != null && planUsage >= 0 && planLimit
      ? planUsage.toString() + ' / ' + planLimit.toString()
      : '-';

  //「機能のアクティベート」ボタンの表示有無
  // deviceLimitTypeが2の場合はカメラ有償機能のプラン
  const activateUsageAndLimit = deviceLimitType === 2;

  //「購入者に返却」ボタンの有効無効
  // 有償機能プラン且つ1件でも使用していた場合は無効にする
  const isReturnToBuyer =
    activateUsageAndLimit && planUsage != null && planUsage > 0 ? false : true;

  //「他ユーザ管理 / 解約手続」
  const lendingUserButtonText =
    glossary.glx(ContractGL.OtherUserManagement) +
    ' / ' +
    glossary.glx(ContractGL.Cancel);

  // 購入ステータス文字列を生成
  const purchaseStatusText = _createPurchaseStatusText(
    validityPeriod,
    expirationDatetime,
    expirationGraceDays,
    state,
    renewalType,
    glossary
  );

  const keyAndValue = (key: string, separator: string, value: string) => {
    return (
      <div className="textKeyAndValue">
        <div className="key">{key}</div>
        <div className="separator">{separator}</div>
        <div className="value">{value}</div>
      </div>
    );
  };
  const info = React.useMemo(() => {
    return window.innerWidth > 639 ||
      document.documentElement.clientWidth > 639 ? (
      <div className="info" data-test={`${dataTest}`}>
        <Image
          type="icon"
          imageSrc={imageSrc}
          data-test={`${dataTest}-image`}
        />
        <div className="contractInfo" data-test={`${dataTest}-contractInfo`}>
          <Title type="title" data-test={`${dataTest}-title`}>
            {isDemo == false ? contractPlanName : contractPlanName + ' (DEMO)'}
          </Title>
          {purchaseStatusText != '-' ? (
            <Message
              type="message"
              subType="canceled"
              data-test={`${dataTest}-canceledMessage`}
            >
              {purchaseStatusText}
            </Message>
          ) : null}
          <div className="textInfo">
            <div className="textInfoLeft">
              {!isCanceled
                ? keyAndValue(
                    glossary.glx(ContractGL.OperationDatetime),
                    ':',
                    dispOperationDatetime
                  )
                : null}
              {keyAndValue(expirationDateTitle, ':', dispExpirationDatetime)}
              {usageAndLimit != '-'
                ? keyAndValue(
                    glossary.glx(ContractGL.TotalUsage),
                    ':',
                    usageAndLimit
                  )
                : null}
              {lendingUsageAndLimit
                ? keyAndValue(
                    glossary.glx(ContractGL.UseByOtherUsers),
                    ':',
                    lendingUsageAndLimit
                  )
                : null}
              {planUsage !== null && activateUsageAndLimit
                ? keyAndValue(
                    glossary.glx(ContractGL.NumberOfPlanUsage),
                    ':',
                    planUsage?.toString()
                  )
                : null}
            </div>
            <div className="textInfoRight">
              {!isLoanUser
                ? keyAndValue(
                    glossary.glx(ContractGL.ContractPlanPrice),
                    ':',
                    priceLabel
                  )
                : null}
              {!isLoanUser
                ? keyAndValue(
                    glossary.glx(ContractGL.VendorPurchaseId),
                    ':',
                    vendorPurchaseId
                  )
                : null}
              {!isLoanUser && purchaseQuantity
                ? keyAndValue(
                    glossary.glx(ContractGL.NumberOfOrders),
                    ':',
                    purchaseQuantity.toString()
                  )
                : null}
            </div>
          </div>
          <div className="contractLinkAndButton">
            <LinkText
              type="detailLink"
              data-test={`${dataTest}-detailLink`}
              onClick={onClickDetail}
            >
              {glossary.glx(ContractGL.Detail)} &gt;
            </LinkText>
            <div className="contractLinkAndButtonRight">
              <Button
                type="deviceButton"
                data-test={`${dataTest}-deviceButton`}
                onClick={onClickDevice}
                style={{
                  display: planUsageAndLimit != '-' ? 'block' : 'none'
                }}
              >
                {glossary.glx(ContractGL.DeviceManagement)}
              </Button>
              {activateUsageAndLimit ? (
                <Button
                  type="deviceButton"
                  data-test={`${dataTest}-activateButton`}
                  onClick={onClickActivate}
                  style={{
                    display: activateUsageAndLimit ? 'block' : 'none'
                  }}
                >
                  {glossary.glx(ContractGL.DeviceActivate)}
                </Button>
              ) : null}
              {!isLoanUser ? (
                <Button
                  type="toolButton"
                  className="lendingUserButton"
                  data-test={`${dataTest}-lendingUserButton`}
                  onClick={onClickLendingUser}
                  style={{
                    display: isLendingPlan ? 'block' : 'none'
                  }}
                >
                  {lendingUserButtonText}
                </Button>
              ) : (
                <Button
                  type="cancelButton"
                  className={
                    isReturnToBuyer
                      ? 'lendingUserButton'
                      : 'lendingUserButtonDisabled'
                  }
                  data-test={`${dataTest}-lendingUserButton`}
                  onClick={onClickCancellationOfUse}
                  style={{
                    display: isLendingPlan ? 'block' : 'none',
                    pointerEvents: isReturnToBuyer ? 'auto' : 'none'
                  }}
                >
                  {glossary.glx(ContractGL.ReturnToBuyer)}
                </Button>
              )}
              <Button
                type="cancelButton"
                data-test={`${dataTest}-cancelButton`}
                onClick={onClickCancel}
                style={{
                  display:
                    isValidSubscriptionItem && !isDemo && !isLendingPlan
                      ? 'block'
                      : 'none'
                }}
              >
                {glossary.glx(ContractGL.Cancel)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className="info" data-test={`${dataTest}`}>
        <Image
          type="icon"
          imageSrc={imageSrc}
          data-test={`${dataTest}-image`}
        />
        <Title type="title" data-test={`${dataTest}-title`}>
          {isDemo == false ? contractPlanName : contractPlanName + ' (DEMO)'}
        </Title>
        {purchaseStatusText != '-' ? (
          <Message
            type="message"
            subType="canceled"
            data-test={`${dataTest}-canceledMessage`}
          >
            {purchaseStatusText}
          </Message>
        ) : null}
        <div className="textInfo" data-test={`${dataTest}-textInfo`}>
          {!isCanceled
            ? keyAndValue(
                glossary.glx(ContractGL.OperationDatetime),
                ':',
                dispOperationDatetime
              )
            : null}
          {keyAndValue(expirationDateTitle, ':', dispExpirationDatetime)}
          {!isLoanUser
            ? keyAndValue(
                glossary.glx(ContractGL.ContractPlanPrice),
                ':',
                priceLabel
              )
            : null}
          {!isLoanUser
            ? keyAndValue(
                glossary.glx(ContractGL.VendorPurchaseId),
                ':',
                vendorPurchaseId
              )
            : null}
          {!isLoanUser && purchaseQuantity
            ? keyAndValue(
                glossary.glx(ContractGL.NumberOfOrders),
                ':',
                purchaseQuantity.toString()
              )
            : null}
          {planUsage != null && activateUsageAndLimit
            ? keyAndValue(
                glossary.glx(ContractGL.NumberOfPlanUsage),
                ':',
                planUsage?.toString()
              )
            : null}
          {usageAndLimit != '-'
            ? keyAndValue(
                glossary.glx(ContractGL.TotalUsage),
                ':',
                usageAndLimit
              )
            : null}
          {lendingUsageAndLimit
            ? keyAndValue(
                glossary.glx(ContractGL.UseByOtherUsers),
                ':',
                lendingUsageAndLimit
              )
            : null}
        </div>
        <div className="contractLinkAndButton">
          <div></div>
          <LinkText
            type="detailLink"
            data-test={`${dataTest}-detailLink`}
            onClick={onClickDetail}
          >
            {glossary.glx(ContractGL.Detail)} &gt;
          </LinkText>
          <Button
            type="deviceButton"
            data-test={`${dataTest}-deviceButton`}
            onClick={onClickDevice}
            style={{
              display: planUsageAndLimit != '-' ? 'block' : 'none'
            }}
          >
            {glossary.glx(ContractGL.DeviceManagement)}
          </Button>
          {activateUsageAndLimit ? (
            <Button
              type="deviceButton"
              data-test={`${dataTest}-activateButton`}
              onClick={onClickActivate}
              style={{
                display: activateUsageAndLimit ? 'block' : 'none'
              }}
            >
              {glossary.glx(ContractGL.DeviceActivate)}
            </Button>
          ) : null}
          {!isLoanUser ? (
            <Button
              type="toolButton"
              className="lendingUserButton"
              data-test={`${dataTest}-lendingUserButton`}
              onClick={onClickLendingUser}
              style={{
                display: isLendingPlan ? 'block' : 'none'
              }}
            >
              {lendingUserButtonText}
            </Button>
          ) : (
            <Button
              type="cancelButton"
              className={
                isReturnToBuyer
                  ? 'lendingUserButton'
                  : 'lendingUserButtonDisabled'
              }
              data-test={`${dataTest}-lendingUserButton`}
              onClick={onClickCancellationOfUse}
              style={{
                display: isLendingPlan ? 'block' : 'none',
                pointerEvents: isReturnToBuyer ? 'auto' : 'none'
              }}
            >
              {glossary.glx(ContractGL.ReturnToBuyer)}
            </Button>
          )}
          <Button
            type="cancelButton"
            data-test={`${dataTest}-cancelButton`}
            onClick={onClickCancel}
            style={{
              display:
                isValidSubscriptionItem && !isDemo && !isLendingPlan
                  ? 'block'
                  : 'none'
            }}
          >
            {glossary.glx(ContractGL.Cancel)}
          </Button>
        </div>
      </div>
    );
  }, [width, height]);

  return info;
};

/**
 * 各契約中プランのPresentation Component
 */
export const ContractPlan: React.FC<ContractPlanProps> = ({
  'data-test': dataTest,
  ...props
}) => {
  return (
    <div className="contractPlan" data-test={dataTest}>
      <ContractPlanInfo {...props} data-test={`${dataTest}-info`} />
    </div>
  );
};
